import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import axios from '../../Api';

const MyChecks = () => {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalChecks, setTotalChecks] = useState(0);
  const checksPerPage = 10;
  const [statistics, setStatistics] = useState({
    total: 0,
    validated: 0,
    rejected: 0,
    hold: 0,
    average_rating: 0
  });
  const [filters, setFilters] = useState({
    action: '',
    rating: '',
    from_date: '',
    to_date: ''
  });

  useEffect(() => {
    fetchChecks();
  }, [currentPage, filters]);

  const fetchChecks = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: checksPerPage
      };
      if (filters.action) params.action = filters.action;
      if (filters.rating) params.rating = filters.rating;
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;

      const response = await axios.get('/control/checks', { params });
      setChecks(response.data.data || []);
      setTotalPages(response.data.last_page || 1);
      setTotalChecks(response.data.total || response.data.data?.length || 0);
      
      // Set statistics from API response (global statistics, not just current page)
      if (response.data.statistics) {
        setStatistics(response.data.statistics);
      } else {
        // Fallback: calculate from current page if statistics not provided
        setStatistics({
          total: response.data.total || 0,
          validated: (response.data.data || []).filter(c => c.action === 'validate').length,
          rejected: (response.data.data || []).filter(c => c.action === 'reject').length,
          hold: (response.data.data || []).filter(c => c.action === 'hold').length,
          average_rating: (response.data.data || []).length > 0
            ? ((response.data.data || []).reduce((sum, c) => sum + (c.rating || 0), 0) / (response.data.data || []).length).toFixed(1)
            : 0
        });
      }
    } catch (error) {
      console.error('Error fetching checks:', error);
      alert('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchChecks();
  };

  const handleResetFilters = () => {
    setFilters({
      action: '',
      rating: '',
      from_date: '',
      to_date: ''
    });
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getActionBadge = (action) => {
    const badges = {
      validate: { bg: 'bg-green-100', text: 'text-green-800', label: 'Validé' },
      reject: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejeté' },
      hold: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En attente' }
    };
    const badge = badges[action] || { bg: 'bg-gray-100', text: 'text-gray-800', label: action };
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Mon Historique de Contrôles</h1>
        <p className="text-gray-600 mt-2">Consultez tous vos contrôles effectués</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Filtres</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="">Toutes</option>
              <option value="validate">Validé</option>
              <option value="reject">Rejeté</option>
              <option value="hold">En attente</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
            <select
              value={filters.rating}
              onChange={(e) => handleFilterChange('rating', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="">Toutes</option>
              <option value="5">5 étoiles</option>
              <option value="4">4 étoiles</option>
              <option value="3">3 étoiles</option>
              <option value="2">2 étoiles</option>
              <option value="1">1 étoile</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date début</label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => handleFilterChange('from_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date fin</label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => handleFilterChange('to_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Appliquer
          </button>
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500 uppercase">Total</div>
          <div className="mt-2 text-3xl font-bold text-gray-800">{statistics.total || totalChecks}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500 uppercase">Validés</div>
          <div className="mt-2 text-3xl font-bold text-green-600">
            {statistics.validated || 0}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500 uppercase">Rejetés</div>
          <div className="mt-2 text-3xl font-bold text-red-600">
            {statistics.rejected || 0}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500 uppercase">Note Moyenne</div>
          <div className="mt-2 text-3xl font-bold text-red-600">
            {statistics.average_rating || 0}/5
          </div>
        </div>
      </div>

      {/* Checks Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date/Heure
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Enquêteur
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID Enquête
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID de la réponse
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tel de client
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Note
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Correction
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                Commentaire
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {checks.map((check) => (
              <tr key={check.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {new Date(check.checked_at).toLocaleString('fr-FR')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {check.assignment?.interviewer?.name || check.assignment?.supervisor?.name || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-500">
                    {check.assignment?.interviewer?.ref || check.assignment?.supervisor?.ref || ''}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {check.assignment?.survey ? (
                    <div>
                      <div className="font-medium">{check.assignment.survey.title || 'N/A'}</div>
                      <div className="text-xs text-gray-500">ID: {check.assignment.survey.limesurvey_id || 'N/A'}</div>
                    </div>
                  ) : (
                    'N/A'
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {check.assignment?.submission?.response_id || check.assignment?.f2f_submission?.response_id || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {check.tel_value || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className="text-yellow-400">{'★'.repeat(check.rating)}</span>
                    <span className="text-gray-300">{'★'.repeat(5 - check.rating)}</span>
                    <span className="ml-2 text-sm text-gray-600">{check.rating}/5</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getActionBadge(check.action)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {check.correction_applied ? (
                    <span className="text-orange-600">Oui</span>
                  ) : (
                    <span className="text-gray-400">Non</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 min-w-[200px]">
                  <div className="max-w-md break-words">
                    {check.comment || '-'}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {checks.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Aucun contrôle trouvé
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white rounded-lg shadow p-4">
          <div className="flex items-center text-xs sm:text-sm text-gray-700 whitespace-nowrap">
            <span>
              Affichage de {((currentPage - 1) * checksPerPage) + 1} à {Math.min(currentPage * checksPerPage, totalChecks)} sur {totalChecks} contrôles
            </span>
          </div>
          
          <div className="flex items-center justify-center sm:justify-end gap-2 flex-wrap">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-2 sm:px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
            </button>
            
            <div className="flex gap-1 flex-wrap justify-center">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors flex-shrink-0 ${
                      currentPage === pageNum
                        ? 'bg-red-600 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-2 sm:px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            >
              <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyChecks;

